import React, { useContext, useRef, useEffect, useState } from 'react';
import { CheckboxField } from '@trp-beacon-ui-kit/react-controls';
import { RewardsContext } from '../../context/RewardsContext/RewardsContext';
import RDFTermsLegal from '../../components/RDFTermsLegal/RDFTermsLegal';
import TermsRenderer from '../../components/TermsRenderer/TermsRenderer';
import PhoneNumber from '../../components/PhoneNumber/PhoneNumber';
import RDFProspLinksSection from '../../components/RDFProspLinksSection/RDFProspLinksSection';
import { rdfProspIntroText } from './utils';
import styles from './style.css';
import { MFBeaconButton } from '../../components/Beacon/BeaconComponents';
import Heading from '../../components/Typography/Heading/Heading';
import { generateGeneralTestID } from '../../generateAutomationTestIDs';

const RDFTermsScreen = ({ next, back, appState, formProps }) => {
  const termsWrapperRef = useRef(null);
  const [advisoryServiceConsent, setAdvisoryServiceConsent] = useState(false);
  const [electronicAccessConsent, setElectronicAccessConsent] = useState(false);

  const {
    store: { amount, termsData = {}, loadedData }
  } = appState;
  const { rdf = {}, electronicAccess = {}, fiduciary = {} } = termsData;
  const { rdfTermsID = '', rdfTermsAndConditionParagraphs = [] } = rdf;
  const {
    electronicAccessTermsID = '',
    electronicAccessTermsAndConditionParagraphs = []
  } = electronicAccess;
  const { fiduciaryTermsAndConditionParagraphs = [] } = fiduciary;

  const { rewardsAvailable = {} } = useContext(RewardsContext) || {};

  const { iClassFunds: iClassFundReward } = rewardsAvailable;

  const { allRetirementDateFundsData = [] } = loadedData;

  useEffect(() => {
    const { current } = termsWrapperRef;
    if (!current) return;

    /**
     * This makes it so links in the returned body from terms service
     * open inside of a new tab.
     */
    /* eslint no-param-reassign: ["error", { "props": true, "ignorePropertyModificationsFor": ["link"] }] */
    [...current.querySelectorAll('a')].forEach(link => {
      link.target = '_blank';
    });
  });

  return (
    <>
      <div className="rdf-terms">
        <Heading text="Agree to the terms" />

        <div className="container" ref={termsWrapperRef}>
          <div className="section-header">I. Key points</div>
          <div className="terms-text">
            <TermsRenderer terms={rdfTermsAndConditionParagraphs} />
          </div>

          <CheckboxField
            className="advisoryServiceConsent"
            data-name={generateGeneralTestID(
              'RDFTerms',
              'AdvisoryServiceConsent'
            )}
            title={
              <div className="checkbox-label">I understand the Key Points</div>
            }
            value={rdfTermsID}
            onChange={() =>
              setAdvisoryServiceConsent(
                prevAdvisoryServiceConsent => !prevAdvisoryServiceConsent
              )
            }
            {...formProps.advisoryServiceConsent}
            defaultChecked={false}
          />

          <div className="section-header">
            II. Consent to Electronic Access &amp; Delivery
          </div>
          <div className="terms-text">
            <TermsRenderer
              terms={electronicAccessTermsAndConditionParagraphs}
            />
          </div>
          <CheckboxField
            className="electronicAccessConsent"
            data-name={generateGeneralTestID(
              'RDFTerms',
              'ElectronicAccessConsent'
            )}
            title={
              <div className="checkbox-label">
                I agree to this Consent to Electronic Access and Delivery
              </div>
            }
            value={electronicAccessTermsID}
            onChange={() =>
              setElectronicAccessConsent(
                prevElectronicAccessConsent => !prevElectronicAccessConsent
              )
            }
            {...formProps.electronicAccessConsent}
            defaultChecked={false}
          />

          <div className="section-header">
            III. Prospectuses for Retirement Funds
          </div>
          <div className="terms-text">
            <TermsRenderer terms={rdfProspIntroText} />
            <RDFProspLinksSection
              amount={amount}
              iClassRewards={iClassFundReward}
              allRetirementDateFundsData={allRetirementDateFundsData}
            />
          </div>

          <div className="section-header">
            IV. Agreement and Supplement, ADV, Forms CRS and Privacy Notice
          </div>
          <div className="terms-text">
            <TermsRenderer terms={fiduciaryTermsAndConditionParagraphs} />
          </div>
        </div>

        {(!advisoryServiceConsent || !electronicAccessConsent) && (
          <div className="consent-remind">
            Please check both boxes to activate the &quot;I Agree&quot; button.
          </div>
        )}

        <div className="button-container">
          <MFBeaconButton
            variant="primary"
            size="normal"
            id="RDFTermsAgreement"
            data-name={generateGeneralTestID(
              'RDFTerms',
              'TermsAgreementConsent'
            )}
            className="beacon-button-margin-override"
            onClick={next()}
            disabled={!advisoryServiceConsent || !electronicAccessConsent}
            label="I Agree"
          />

          <MFBeaconButton
            variant="secondary"
            size="normal"
            id="RDFTermsBack"
            className="beacon-button-margin-override"
            data-name={generateGeneralTestID('RDFTerms', 'BackButton')}
            onClick={back()}
            label="Back"
          />
        </div>

        <PhoneNumber />
      </div>

      <RDFTermsLegal />
      <div className="legal-reference-number">
        <p>202409-3813205</p>
      </div>
      <style jsx>{styles}</style>
    </>
  );
};

export default RDFTermsScreen;
